import { autoUpdate, flip, useFloating } from "@floating-ui/react-dom";
import Downshift from "downshift";
import React, { useEffect, useLayoutEffect, useState } from "react";
import styled from "styled-components";
import Portal from "../../common/components/Portal";
import {
    defaultRadius,
    distances,
    grid,
    palette,
    placeholderColor,
    shadows,
    zIndex,
} from "../../styles/constants";
import { cn } from "../../util/css";
import Input from "./Input";

export interface DropdownExampleProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    options: string[];
    disabled?: boolean;
}

const digitsAndPlusRegex = /^[0-9+]*$/;

const MIN_HEIGHT_DROPDOWN_BUTTON = 40;
const DROPDOWN_RADIUS_OFFSET = 2;

const CountryPrefix = ({
    value,
    options,
    onChange,
    disabled,
    onBlur,
}: DropdownExampleProps) => {
    const selectedOption = options.find((option) => option === value) || "";
    const [prefix, setPrefix] = useState(selectedOption);

    useEffect(() => {
        setPrefix(value);
    }, [value]);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!digitsAndPlusRegex.test(e.target.value)) {
            e.target.value = e.target.value.replace(/[^0-9+]/g, "");
        }
        setPrefix(e.target.value);
        onChange(e.target.value);
    };

    return (
        <Wrapper>
            <Downshift
                onSelect={(selection) => {
                    setPrefix(selection);
                    onChange(selection);
                }}
                onChange={(selection) => {
                    setPrefix(selection);
                    onChange(selection);
                }}
                itemToString={(item) => (item ? item : "")}
            >
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    isOpen,
                    getToggleButtonProps,
                    selectedItem,
                    highlightedIndex,
                }) => {
                    return (
                        <div>
                            <FloatingWrapper isOpen={isOpen}>
                                {({ refs, placement, floatingStyles }) => (
                                    <>
                                        {!isOpen && (
                                            <OpenButton
                                                {...getToggleButtonProps()}
                                                type="button"
                                                aria-label={"toggle menu"}
                                                disabled={disabled}
                                                className={
                                                    prefix ? "" : "placeholder"
                                                }
                                            >
                                                {prefix}
                                            </OpenButton>
                                        )}
                                        {isOpen && (
                                            <Input
                                                {...getInputProps({
                                                    placeholder: "+00",
                                                    onChange: onInputChange,
                                                    name: "phoneNumberDropdown",
                                                    onBlur:
                                                        onBlur || (() => {}),
                                                })}
                                                value={prefix}
                                                autoFocus
                                                disabled={disabled}
                                                autocomplete="off"
                                                mb="0"
                                            />
                                        )}
                                        <DropdownWrapper
                                            ref={refs.setReference}
                                        >
                                            <Portal>
                                                <Dropdown
                                                    {...getMenuProps(
                                                        {
                                                            disabled: disabled,
                                                            ref: refs.setFloating,
                                                        },
                                                        {
                                                            suppressRefError: true,
                                                        },
                                                    )}
                                                    className={cn(
                                                        !isOpen && "hidden",
                                                    )}
                                                    style={{
                                                        ...floatingStyles,
                                                        width:
                                                            refs.reference.current?.getBoundingClientRect()
                                                                ?.width || 0,
                                                        marginTop:
                                                            placement === "top"
                                                                ? -MIN_HEIGHT_DROPDOWN_BUTTON
                                                                : undefined,
                                                    }}
                                                >
                                                    {isOpen && (
                                                        <DropdownInner>
                                                            {options
                                                                .filter(
                                                                    (o, i, a) =>
                                                                        a.findIndex(
                                                                            (
                                                                                o2,
                                                                            ) =>
                                                                                o2 ===
                                                                                o,
                                                                        ) === i,
                                                                )
                                                                .map(
                                                                    (
                                                                        item,
                                                                        index,
                                                                    ) => {
                                                                        return (
                                                                            <DropdownItem
                                                                                {...getItemProps(
                                                                                    {
                                                                                        key: item,
                                                                                        index,
                                                                                        item,
                                                                                    },
                                                                                )}
                                                                                className={cn(
                                                                                    item ===
                                                                                        selectedItem?.value &&
                                                                                        "selected",
                                                                                    highlightedIndex ===
                                                                                        index &&
                                                                                        "highlighted",
                                                                                    disabled &&
                                                                                        "disabled",
                                                                                )}
                                                                                type="button"
                                                                            >
                                                                                {
                                                                                    item
                                                                                }
                                                                            </DropdownItem>
                                                                        );
                                                                    },
                                                                )}
                                                        </DropdownInner>
                                                    )}
                                                </Dropdown>
                                            </Portal>
                                        </DropdownWrapper>
                                    </>
                                )}
                            </FloatingWrapper>
                        </div>
                    );
                }}
            </Downshift>
        </Wrapper>
    );
};

type FloatingWrapperProps = {
    isOpen: boolean;
    children: (props: ReturnType<typeof useFloating>) => React.ReactNode;
};

const FloatingWrapper = ({ isOpen, children }: FloatingWrapperProps) => {
    const floatingProps = useFloating({
        open: isOpen,
        strategy: "fixed",
        middleware: [flip()],
    });

    useLayoutEffect(() => {
        if (
            isOpen &&
            floatingProps.refs.reference.current &&
            floatingProps.refs.floating.current
        ) {
            return autoUpdate(
                floatingProps.refs.reference.current,
                floatingProps.refs.floating.current,
                floatingProps.update,
            );
        }
    }, [
        isOpen,
        floatingProps.update,
        floatingProps.refs.floating,
        floatingProps.refs.reference,
    ]);

    return <>{children(floatingProps)}</>;
};

const Wrapper = styled.div`
    min-width: ${grid.spans.span1};
    width: ${distances.large40};
    margin-right: ${distances.tiny};
    max-height: 40px;
`;

const OpenButton = styled.button`
    border: none;
    outline: none;
    border: 1px solid ${palette.neutral[400]};
    border-radius: ${defaultRadius};
    background: ${palette.white};
    padding: ${distances.tiny} ${distances.small12};
    width: 100%;
    -moz-appearance: textfield;
    text-align: left;
    min-height: 40px;
    cursor: text;

    ${(props) =>
        props.prefix
            ? `padding-left: calc(${distances.small} + ${props.prefix.length * 12}px);`
            : ""}

    &::-webkit-inner-spin-button {
        display: none;
        -webkit-appearance: none;
    }

    &::placeholder {
        color: ${placeholderColor};
        opacity: 1;
    }

    &:focus {
        outline: none;
        border: 1px solid ${palette.primary[300]};
        box-shadow: 0 0 0 3px ${palette.primary[100]};
    }
    &.invalid,
    &:focus .invalid {
        border: 1px solid ${palette.destructive[300]};
    }

    &.invalid:focus {
        box-shadow: 0 0 0 3px ${palette.destructive[100]};
    }

    &.valid,
    &:focus .valid {
        border: 1px solid ${palette.success[300]};
    }

    &.valid:focus {
        box-shadow: 0 0 0 3px ${palette.success[100]};
    }

    &:disabled {
        background: ${palette.neutral[50]};
        border: 1px solid ${palette.neutral[200]};
        color: ${palette.neutral[500]};
    }
`;

const DropdownWrapper = styled.div`
    width: inherit;
`;

const Dropdown = styled.div.attrs((props) => ({
    ...props,
    role: "listbox",
}))`
    position: absolute;
    width: inherit;
    padding-left: 0;
    background: ${palette.white};
    box-shadow: ${shadows.large};
    max-height: 425px;
    overflow-y: auto;
    z-index: ${zIndex.top};
    margin-top: ${distances.micro};
    border-radius: ${defaultRadius};
    top: 0;
    visibility: visible;
    &.hidden {
        opacity: 0;
        box-shadow: none;
        pointer-events: none;
        visibility: hidden;
    }
    transition:
        opacity 150ms cubic-bezier(0.2, 0, 0.2, 1),
        box-shadow 150ms cubic-bezier(0.2, 0, 0.2, 1);
    border: 1px solid ${palette.neutral[200]};
    outline: none;
`;

const DropdownInner = styled.div`
    position: relative;
    padding-block: ${distances.micro};
`;

const DropdownItem = styled.li.attrs((props) => ({
    ...props,
    role: "option",
}))`
    appearance: none;
    list-style: none;
    font-size: 14px;
    margin-inline: ${distances.micro};
    padding: ${distances.small12} ${distances.small12};
    white-space: nowrap;
    cursor: pointer;
    border-radius: ${Number.parseInt(defaultRadius) - DROPDOWN_RADIUS_OFFSET}px;
    &.selected {
        background-color: ${palette.primary[50]};
        color: ${palette.primary[500]};
    }
    &:hover,
    &.highlighted,
    &:focus {
        background-color: ${palette.primary[500]};
        color: white;
        & > * > * {
            color: white;
        }
        span {
            color: ${palette.primary[300]};
        }
        &.disabled {
            background-color: ${palette.neutral[50]};
            color: ${palette.neutral[300]};
        }
    }
    &.disabled {
        color: ${palette.neutral[300]};
        cursor: not-allowed;
    }
    &.hidden {
        display: none;
    }
    & > * {
        pointer-events: none;
    }

    display: flex;
    flex-direction: column;
`;

export default CountryPrefix;
